import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import { TextInput, Select, Textarea, Checkbox, Alert } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useMutation, useQuery } from "@apollo/client";
import { useForm } from "@mantine/form";
import { zod4Resolver } from "mantine-form-zod-resolver";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendar } from "@fortawesome/sharp-regular-svg-icons";
import { subYears, differenceInYears } from "date-fns";
import { z } from "zod";
import { gql } from "@/__generated__";
import {
  Condition,
  Medication,
} from "shared/src/data/medication-condition-pairs";
import {
  SMS_NOTIFICATIONS_TRANSACTIONAL_CONSENT_LANGUAGE,
  SMS_NOTIFICATIONS_MARKETING_CONSENT_LANGUAGE,
} from "shared/src/constants/smsConsentLanguage";
import {
  Sex,
  Pronouns,
  CaregiverRelationship,
  SavePatientAndCaregiverInfoOnReferralInput,
  AppealSupportingDocumentType,
  AppealSupportingDocumentFileType,
} from "@/__generated__/graphql";
import { PatientConsentSection } from "@/components/PatientConsent/PatientConsentSection";
import { uploadFileToUrl } from "@/utils/fileRequests";
import { getPageCountForFile } from "@/utils/pageCount";
import { getClientSafeErrorMessage } from "shared/src/utils/apolloErrorUtils";
import Loader from "@/components/shared/Loader";
import statesData from "@/data/states.json";
import styles from "./patient-info-form.module.scss";
import { useFormDirtyTracking } from "@/hooks/useFormDirtyTracking";

export type PatientInfoFormHandle = {
  submit: () => Promise<boolean>;
};

const GET_PROVIDER = gql(`
  query GetProviderForPatientForm {
    provider {
      providerId
      hasSignedBAA
    }
  }
`);

const GET_APPEAL_BY_ID = gql(`
  query GetAppealByIdForPatientForm($appealId: ID!) {
    getAppealById(appealId: $appealId) {
      appealId
      referringProviderName
      referralPatientEmail
      patient {
        firstName
        lastName
        dateOfBirth
        sex
        pronouns
        address {
          state
        }
        phone
        hasTransactionalSmsConsent
        hasMarketingSmsConsent
      }
      caregiver {
        firstName
        lastName
        dateOfBirth
        relationship
      }
      referral {
        providerMessage
        hasPatientConsent
      }
      denial {
        condition
        medicationBrandName
        denialReason
      }
      supportingDocuments(includeAll: true) {
        documentId
        documentType
        files {
          fileId
          fileName
          fileType
          url
        }
      }
    }
  }
`);

const SAVE_PATIENT_AND_CAREGIVER_INFO_ON_REFERRAL = gql(`
  mutation SavePatientAndCaregiverInfoOnReferralFromForm($input: SavePatientAndCaregiverInfoOnReferralInput!) {
    savePatientAndCaregiverInfoOnReferral(input: $input)
  }
`);

const UPSERT_CONSENT_DOCUMENT = gql(`
  mutation UpsertConsentDocumentFromForm(
    $appealId: ID!
    $document: UpsertAppealSupportingDocumentInput!
  ) {
    upsertAppealSupportingDocument(appealId: $appealId, document: $document) {
      uploadUrls
    }
  }
`);

export const PHONE_REGEX =
  /^(\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/;

export const MAX_AGE_YEARS = 120;

export const MIN_AGE_YEARS = 0;

export const VALIDATION_MESSAGES = {
  FIRST_NAME_REQUIRED: "First name is required",
  LAST_NAME_REQUIRED: "Last name is required",
  DATE_OF_BIRTH_REQUIRED: "Date of birth is required",
  DATE_OF_BIRTH_FUTURE: "Date of birth cannot be in the future",
  AGE_RANGE: `Patient must be between ${MIN_AGE_YEARS} and ${MAX_AGE_YEARS} years old`,
  STATE_REQUIRED: "State of residence is required",
  EMAIL_REQUIRED: "Email address is required",
  EMAIL_INVALID: "Please enter a valid email address",
  PHONE_INVALID: "Enter a valid phone number",
  CAREGIVER_REQUIRED_FOR_MINOR:
    "Patients under 18 years old require a caregiver",
  CAREGIVER_FIRST_NAME_REQUIRED: "Caregiver first name is required",
  CAREGIVER_LAST_NAME_REQUIRED: "Caregiver last name is required",
  CAREGIVER_DOB_FUTURE: "Caregiver date of birth cannot be in the future",
  CAREGIVER_AGE_RANGE: "Caregiver must be between 18 and 120 years old",
  CAREGIVER_RELATIONSHIP_REQUIRED: "Relationship to patient is required",
  CAREGIVER_EMAIL_REQUIRED: "Caregiver email address is required",
  CAREGIVER_EMAIL_INVALID: "Please enter a valid caregiver email address",
  SEX_REQUIRED_WITH_CAREGIVER:
    "Patient sex is required when caregiver information is provided",
  PRONOUNS_REQUIRED_WITH_CAREGIVER:
    "Patient pronouns are required when caregiver information is provided",
} as const;

const calculateAge = (dateOfBirth: Date | null): number | null => {
  if (!dateOfBirth) {
    return null;
  }
  return differenceInYears(new Date(), dateOfBirth);
};

export const isCaregiverAllowedForCondition = (
  condition: string | null | undefined,
  medicationBrandName: string | null | undefined,
): boolean => {
  if (!condition) {
    return true;
  }

  const alwaysAllowedConditions = [
    Condition.CSID,
    Condition.PANS,
    Condition.PANDAS,
  ];

  if (alwaysAllowedConditions.includes(condition as Condition)) {
    return true;
  }

  const dupixentConditions = [
    Condition.ASTHMA,
    Condition.ATOPIC_DERMATITIS,
    Condition.CHRONIC_RHINOSINUSITIS,
    Condition.EOSINOPHILIC_ESOPHAGITIS,
  ];

  if (dupixentConditions.includes(condition as Condition)) {
    return medicationBrandName === Medication.DUPIXENT;
  }

  return false;
};

type ValidationIssue = {
  code: "custom";
  message: string;
  path: string[];
} | null;

const validatePhoneNumberFormat = (phoneNumber?: string): ValidationIssue => {
  const hasPhoneNumber = Boolean(phoneNumber?.trim().length);

  if (hasPhoneNumber && phoneNumber && !PHONE_REGEX.test(phoneNumber.trim())) {
    return {
      code: "custom",
      message: VALIDATION_MESSAGES.PHONE_INVALID,
      path: ["phoneNumber"],
    };
  }

  return null;
};

const validatePhoneRequiredWhenSmsConsent = (
  phoneNumber: string | undefined,
  hasConsentedToSMSNotifications: boolean | undefined,
  hasConsentedToMarketingSMS: boolean | undefined,
): ValidationIssue => {
  const hasPhoneNumber = Boolean(phoneNumber?.trim().length);
  const hasSmsConsent =
    hasConsentedToSMSNotifications || hasConsentedToMarketingSMS;

  if (hasSmsConsent && !hasPhoneNumber) {
    return {
      code: "custom",
      message: "Patient mobile number is required to send SMS notifications",
      path: ["phoneNumber"],
    };
  }

  return null;
};

const validateMinorRequiresCaregiver = (
  dateOfBirth: Date | null,
  hasCaregiverInfo: boolean,
): ValidationIssue => {
  const patientAge = calculateAge(dateOfBirth);

  if (patientAge !== null && patientAge < 18 && !hasCaregiverInfo) {
    return {
      code: "custom",
      message: VALIDATION_MESSAGES.CAREGIVER_REQUIRED_FOR_MINOR,
      path: ["dateOfBirth"],
    };
  }

  return null;
};

const validateEmailWhenNoCaregiverRequired = (
  email: string | undefined,
  hasCaregiverInfo: boolean,
): ValidationIssue => {
  if (!hasCaregiverInfo && (!email || !email.trim())) {
    return {
      code: "custom",
      message: VALIDATION_MESSAGES.EMAIL_REQUIRED,
      path: ["email"],
    };
  }

  return null;
};

const validateSexRequiredWithCaregiver = (
  sex: Sex | null,
  hasCaregiverInfo: boolean,
): ValidationIssue => {
  if (hasCaregiverInfo && sex === null) {
    return {
      code: "custom",
      message: VALIDATION_MESSAGES.SEX_REQUIRED_WITH_CAREGIVER,
      path: ["sex"],
    };
  }

  return null;
};

const validatePronounsRequiredWithCaregiver = (
  pronouns: Pronouns | null,
  hasCaregiverInfo: boolean,
): ValidationIssue => {
  if (hasCaregiverInfo && pronouns === null) {
    return {
      code: "custom",
      message: VALIDATION_MESSAGES.PRONOUNS_REQUIRED_WITH_CAREGIVER,
      path: ["pronouns"],
    };
  }

  return null;
};

const validateCaregiverFirstName = (
  caregiverFirstName: string,
  hasCaregiverInfo: boolean,
): ValidationIssue => {
  if (hasCaregiverInfo && !caregiverFirstName.trim()) {
    return {
      code: "custom",
      message: VALIDATION_MESSAGES.CAREGIVER_FIRST_NAME_REQUIRED,
      path: ["caregiverFirstName"],
    };
  }

  return null;
};

const validateCaregiverLastName = (
  caregiverLastName: string,
  hasCaregiverInfo: boolean,
): ValidationIssue => {
  if (hasCaregiverInfo && !caregiverLastName.trim()) {
    return {
      code: "custom",
      message: VALIDATION_MESSAGES.CAREGIVER_LAST_NAME_REQUIRED,
      path: ["caregiverLastName"],
    };
  }

  return null;
};

const validateCaregiverDateOfBirthNotFuture = (
  caregiverDateOfBirth: Date | null,
  hasCaregiverInfo: boolean,
): ValidationIssue => {
  if (
    hasCaregiverInfo &&
    caregiverDateOfBirth !== null &&
    caregiverDateOfBirth > new Date()
  ) {
    return {
      code: "custom",
      message: VALIDATION_MESSAGES.CAREGIVER_DOB_FUTURE,
      path: ["caregiverDateOfBirth"],
    };
  }

  return null;
};

const validateCaregiverAge = (
  caregiverDateOfBirth: Date | null,
  hasCaregiverInfo: boolean,
): ValidationIssue => {
  if (hasCaregiverInfo && caregiverDateOfBirth !== null) {
    const caregiverAge = calculateAge(caregiverDateOfBirth);
    if (caregiverAge !== null && (caregiverAge < 18 || caregiverAge > 120)) {
      return {
        code: "custom",
        message: VALIDATION_MESSAGES.CAREGIVER_AGE_RANGE,
        path: ["caregiverDateOfBirth"],
      };
    }
  }

  return null;
};

const validateCaregiverRelationship = (
  caregiverRelationship: CaregiverRelationship | null,
  hasCaregiverInfo: boolean,
): ValidationIssue => {
  if (hasCaregiverInfo && caregiverRelationship === null) {
    return {
      code: "custom",
      message: VALIDATION_MESSAGES.CAREGIVER_RELATIONSHIP_REQUIRED,
      path: ["caregiverRelationship"],
    };
  }

  return null;
};

const validateEmailRequiredWithCaregiver = (
  email: string | undefined,
  hasCaregiverInfo: boolean,
): ValidationIssue => {
  if (hasCaregiverInfo && (!email || !email.trim())) {
    return {
      code: "custom",
      message: VALIDATION_MESSAGES.CAREGIVER_EMAIL_REQUIRED,
      path: ["email"],
    };
  }

  return null;
};

export const patientInfoFormSchema = z
  .object({
    firstName: z
      .string()
      .min(1, { message: VALIDATION_MESSAGES.FIRST_NAME_REQUIRED }),
    lastName: z
      .string()
      .min(1, { message: VALIDATION_MESSAGES.LAST_NAME_REQUIRED }),
    dateOfBirth: z
      .union([z.date(), z.string(), z.null()])
      .nullable()
      .transform((rawDateOfBirth) => {
        if (!rawDateOfBirth) {
          return null;
        }
        if (rawDateOfBirth instanceof Date) {
          return rawDateOfBirth;
        }
        const parsedDate = new Date(rawDateOfBirth);
        return isNaN(parsedDate.getTime()) ? null : parsedDate;
      })
      .refine((dateOfBirth) => dateOfBirth !== null, {
        message: VALIDATION_MESSAGES.DATE_OF_BIRTH_REQUIRED,
      })
      .refine(
        (dateOfBirth) => {
          if (!dateOfBirth) {
            return true;
          }
          const age = calculateAge(dateOfBirth);
          return age !== null && age >= MIN_AGE_YEARS && age <= MAX_AGE_YEARS;
        },
        {
          message: VALIDATION_MESSAGES.AGE_RANGE,
        },
      )
      .refine(
        (dateOfBirth) => {
          if (!dateOfBirth) {
            return true;
          }
          return dateOfBirth <= new Date();
        },
        {
          message: VALIDATION_MESSAGES.DATE_OF_BIRTH_FUTURE,
        },
      ),
    sex: z.nativeEnum(Sex).nullable(),
    pronouns: z.nativeEnum(Pronouns).nullable(),
    state: z.string().min(1, { message: VALIDATION_MESSAGES.STATE_REQUIRED }),
    email: z
      .string()
      .email({ message: VALIDATION_MESSAGES.EMAIL_INVALID })
      .optional()
      .or(z.literal("")),
    message: z.string(),
    phoneNumber: z.string().optional(),
    hasConsentedToSMSNotifications: z.boolean().optional(),
    hasConsentedToMarketingSMS: z.boolean().optional(),
    hasCaregiverInfo: z.boolean(),
    caregiverFirstName: z.string(),
    caregiverLastName: z.string(),
    caregiverDateOfBirth: z
      .union([z.date(), z.string(), z.null()])
      .nullable()
      .transform((rawCaregiverDateOfBirth) => {
        if (!rawCaregiverDateOfBirth) {
          return null;
        }
        if (rawCaregiverDateOfBirth instanceof Date) {
          return rawCaregiverDateOfBirth;
        }
        const parsedCaregiverDate = new Date(rawCaregiverDateOfBirth);
        return isNaN(parsedCaregiverDate.getTime())
          ? null
          : parsedCaregiverDate;
      }),
    caregiverRelationship: z.nativeEnum(CaregiverRelationship).nullable(),
    hasPatientConsent: z.boolean(),
    consentDocument: z.instanceof(File).nullable(),
  })
  .superRefine((data, schemaContext): void => {
    const validations = [
      validatePhoneNumberFormat(data.phoneNumber),
      validatePhoneRequiredWhenSmsConsent(
        data.phoneNumber,
        data.hasConsentedToSMSNotifications,
        data.hasConsentedToMarketingSMS,
      ),
    ];

    validations.forEach((issue) => {
      if (issue) {
        schemaContext.addIssue(issue);
      }
    });
  })
  .superRefine((data, schemaContext): void => {
    const validations = [
      validateMinorRequiresCaregiver(data.dateOfBirth, data.hasCaregiverInfo),
      validateEmailWhenNoCaregiverRequired(data.email, data.hasCaregiverInfo),
      validateSexRequiredWithCaregiver(data.sex, data.hasCaregiverInfo),
      validatePronounsRequiredWithCaregiver(
        data.pronouns,
        data.hasCaregiverInfo,
      ),
      validateCaregiverFirstName(
        data.caregiverFirstName,
        data.hasCaregiverInfo,
      ),
      validateCaregiverLastName(data.caregiverLastName, data.hasCaregiverInfo),
      validateCaregiverDateOfBirthNotFuture(
        data.caregiverDateOfBirth,
        data.hasCaregiverInfo,
      ),
      validateCaregiverAge(data.caregiverDateOfBirth, data.hasCaregiverInfo),
      validateCaregiverRelationship(
        data.caregiverRelationship,
        data.hasCaregiverInfo,
      ),
      validateEmailRequiredWithCaregiver(data.email, data.hasCaregiverInfo),
    ];

    validations.forEach((issue) => {
      if (issue) {
        schemaContext.addIssue(issue);
      }
    });
  });

const sexOptions = [
  { value: Sex.Female, label: "Female" },
  { value: Sex.Male, label: "Male" },
  { value: Sex.DeclineToAnswer, label: "Decline to answer" },
];

const pronounsOptions = [
  { value: Pronouns.SheHerHers, label: "She/her/hers" },
  { value: Pronouns.HeHimHis, label: "He/him/his" },
  { value: Pronouns.TheyThemTheirs, label: "They/them/theirs" },
];

const relationshipOptions = [
  { value: CaregiverRelationship.Mother, label: "Mother" },
  { value: CaregiverRelationship.Father, label: "Father" },
  {
    value: CaregiverRelationship.OtherLegalGuardian,
    label: "Other legal guardian",
  },
  {
    value: CaregiverRelationship.AuthorizedRepresentative,
    label: "Authorized representative",
  },
];

export type PatientInfoFormValues = {
  firstName: string;
  lastName: string;
  dateOfBirth: Date | null;
  sex: Sex | null;
  pronouns: Pronouns | null;
  state: string;
  phoneNumber: string;
  email: string | undefined;
  message: string;
  hasConsentedToSMSNotifications: boolean;
  hasConsentedToMarketingSMS: boolean;
  hasCaregiverInfo: boolean;
  caregiverFirstName: string;
  caregiverLastName: string;
  caregiverDateOfBirth: Date | null;
  caregiverRelationship: CaregiverRelationship | null;
  hasPatientConsent: boolean;
  consentDocument: File | null;
};

type PatientInfoFormProps = {
  appealId: string;
  readOnly?: boolean;
};

const PatientInfoForm = forwardRef<PatientInfoFormHandle, PatientInfoFormProps>(
  ({ appealId, readOnly = false }, ref) => {
    const [showCaregiverForm, setShowCaregiverForm] = useState(false);
    const { data: providerData } = useQuery(GET_PROVIDER);

    const {
      data: appealQueryData,
      loading: appealQueryLoading,
      error: _appealQueryError,
    } = useQuery(GET_APPEAL_BY_ID, {
      variables: { appealId },
      skip: !appealId,
      fetchPolicy: "no-cache",
    });

    const medicationBrandName =
      appealQueryData?.getAppealById?.denial?.medicationBrandName ?? null;

    const [savePatientAndCaregiverInfo, { loading: savePatientLoading }] =
      useMutation(SAVE_PATIENT_AND_CAREGIVER_INFO_ON_REFERRAL);

    const [upsertDocument, { loading: uploadLoading }] = useMutation(
      UPSERT_CONSENT_DOCUMENT,
    );

    const _mutationLoading = savePatientLoading || uploadLoading;

    const [error, setError] = useState<string | null>(null);
    const [formSubmissionAttempted, setFormSubmissionAttempted] =
      useState<boolean>(false);

    const form = useForm<PatientInfoFormValues>({
      mode: "controlled",
      initialValues: {
        firstName: "",
        lastName: "",
        dateOfBirth: null,
        sex: null,
        pronouns: null,
        state: "",
        phoneNumber: "",
        email: "",
        message: "",
        hasConsentedToSMSNotifications: false,
        hasConsentedToMarketingSMS: false,
        hasCaregiverInfo: false,
        caregiverFirstName: "",
        caregiverLastName: "",
        caregiverDateOfBirth: null,
        caregiverRelationship: null,
        hasPatientConsent: false,
        consentDocument: null,
      },
      validate: zod4Resolver(patientInfoFormSchema),
      validateInputOnBlur: true,
      validateInputOnChange: false,
    });

    const { resetFormDirty } = useFormDirtyTracking(form);

    const DEFAULT_PATIENT_MESSAGE =
      "We've teamed up with Claimable to help fight insurance denials. Good news–we've already started an appeal for you! If you need any support along the way just reach out to Claimable at support@getclaimable.com. They're here to help!";

    const existingConsentDocument = useMemo(() => {
      const documents = appealQueryData?.getAppealById?.supportingDocuments;
      if (!documents) {
        return null;
      }
      const consentDoc = documents.find(
        (doc) =>
          doc.documentType === AppealSupportingDocumentType.PatientConsent,
      );
      if (!consentDoc || !consentDoc.files || consentDoc.files.length === 0) {
        return null;
      }
      return {
        fileName: consentDoc.files[0].fileName,
        url: consentDoc.files[0].url,
      };
    }, [appealQueryData]);

    useEffect(() => {
      if (!appealQueryData?.getAppealById) {
        return;
      }

      const appeal = appealQueryData.getAppealById;
      const patient = appeal.patient;
      const caregiver = appeal.caregiver;
      const referral = appeal.referral;

      if (!patient) {
        return;
      }

      const hasCaregiver = !!caregiver;

      const valuesToSet = {
        firstName: patient.firstName ?? "",
        lastName: patient.lastName ?? "",
        dateOfBirth: patient.dateOfBirth
          ? new Date(patient.dateOfBirth.split("T")[0] + "T00:00:00")
          : null,
        sex: patient.sex ?? null,
        pronouns: patient.pronouns ?? null,
        state: patient.address?.state ?? "",
        phoneNumber: patient.phone ?? "",
        email: appeal.referralPatientEmail ?? "",
        message: referral?.providerMessage || DEFAULT_PATIENT_MESSAGE,
        hasConsentedToSMSNotifications:
          patient.hasTransactionalSmsConsent ?? false,
        hasConsentedToMarketingSMS: patient.hasMarketingSmsConsent ?? false,
        hasCaregiverInfo: hasCaregiver,
        caregiverFirstName: caregiver?.firstName ?? "",
        caregiverLastName: caregiver?.lastName ?? "",
        caregiverDateOfBirth: caregiver?.dateOfBirth
          ? new Date(caregiver.dateOfBirth.split("T")[0] + "T00:00:00")
          : null,
        caregiverRelationship: caregiver?.relationship ?? null,
        hasPatientConsent: referral?.hasPatientConsent ?? false,
        consentDocument: null,
      };

      form.setInitialValues(valuesToSet);
      form.reset();
    }, [appealQueryData]);

    useEffect(() => {
      setShowCaregiverForm(form.values.hasCaregiverInfo);
    }, [form.values.hasCaregiverInfo]);

    function getFirstErrorField(
      errors: Record<string, React.ReactNode>,
    ): string | null {
      const fieldOrder = [
        "firstName",
        "lastName",
        "dateOfBirth",
        "sex",
        "pronouns",
        "state",
        "email",
        "phoneNumber",
        "hasConsentedToSMSNotifications",
        "caregiverFirstName",
        "caregiverLastName",
        "caregiverDateOfBirth",
        "caregiverRelationship",
      ] as const;

      for (const field of fieldOrder) {
        if (errors[field]) {
          return field;
        }
      }

      return null;
    }

    useEffect(() => {
      if (
        !formSubmissionAttempted ||
        !form.errors ||
        Object.keys(form.errors).length === 0
      ) {
        return;
      }

      const firstErrorField = getFirstErrorField(form.errors);
      if (!firstErrorField) {
        return;
      }

      setTimeout(() => {
        const errorElement = form.getInputNode(firstErrorField);
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
          errorElement.focus();
        }
      }, 100);

      setFormSubmissionAttempted(false);
    }, [formSubmissionAttempted, form.errors]);

    const stateOptions = useMemo(
      () =>
        statesData.map((state) => ({
          value: state.stateCode,
          label: state.state,
        })),
      [],
    );

    const handlePhoneNumberChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        form.setFieldValue("phoneNumber", event.target.value);
        form.validateField("hasConsentedToSMSNotifications");
      },
      [form],
    );

    const handleSMSConsentChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        form.setFieldValue(
          "hasConsentedToSMSNotifications",
          event.target.checked,
        );
        form.validateField("phoneNumber");
      },
      [form],
    );

    const handleToggleCaregiverSection = useCallback(() => {
      setShowCaregiverForm((prev) => {
        const newValue = !prev;

        form.setFieldValue("hasCaregiverInfo", newValue);

        if (!newValue) {
          form.setFieldValue("caregiverFirstName", "");
          form.setFieldValue("caregiverLastName", "");
          form.setFieldValue("caregiverDateOfBirth", null);
          form.setFieldValue("caregiverRelationship", null);

          form.clearFieldError("caregiverFirstName");
          form.clearFieldError("caregiverLastName");
          form.clearFieldError("caregiverDateOfBirth");
          form.clearFieldError("caregiverRelationship");
          form.clearFieldError("sex");
          form.clearFieldError("pronouns");
        }

        return newValue;
      });
    }, [form]);

    const handleConsentChange = useCallback(
      (hasConsent: boolean, document?: File | null) => {
        form.setFieldValue("hasPatientConsent", hasConsent);
        if (document !== undefined) {
          form.setFieldValue("consentDocument", document || null);
        }
      },
      [form],
    );

    const mobileNumberLabel = useMemo(
      () => (
        <div className={styles.labelContainer}>
          <span className={styles.labelText}>Mobile Number</span>
          <span className={styles.optionalText}>Optional</span>
        </div>
      ),
      [],
    );

    const dateConstraints = useMemo(() => {
      const today = new Date();
      const maxAgeYears = 120;
      return {
        minDate: subYears(today, maxAgeYears),
        maxDate: today,
      };
    }, []);

    function formatDateForSubmission(
      dateValue: Date | string | null,
    ): string | null {
      if (dateValue === null || dateValue === undefined) {
        return null;
      }
      const date =
        typeof dateValue === "string" ? new Date(dateValue) : dateValue;
      return date ? date.toISOString().split("T")[0] : null;
    }

    const handleFormSubmit = async (
      values: PatientInfoFormValues,
    ): Promise<boolean> => {
      try {
        setError(null);

        const formattedPatientDob = formatDateForSubmission(values.dateOfBirth);
        const messageValue = values.message?.trim() || DEFAULT_PATIENT_MESSAGE;

        const input: SavePatientAndCaregiverInfoOnReferralInput = {
          appealId: appealQueryData?.getAppealById?.appealId ?? "",
          patient: {
            firstName: values.firstName,
            lastName: values.lastName,
            dateOfBirth: formattedPatientDob ?? "",
            state: values.state,
            sex: values.sex ?? undefined,
            pronouns: values.pronouns ?? undefined,
          },
          email: values.email ?? "",
          phoneNumber: values.phoneNumber || undefined,
          consentToSms: values.hasConsentedToSMSNotifications,
          consentToMarketingSms: values.hasConsentedToMarketingSMS,
          providerMessage: messageValue,
          hasPatientConsent: values.hasPatientConsent,
        };

        if (values.hasCaregiverInfo && values.caregiverRelationship) {
          const formattedCaregiverDob = formatDateForSubmission(
            values.caregiverDateOfBirth,
          );
          input.caregiver = {
            firstName: values.caregiverFirstName,
            lastName: values.caregiverLastName,
            dateOfBirth: formattedCaregiverDob,
            relationship: values.caregiverRelationship,
          };
        }

        const result = await savePatientAndCaregiverInfo({
          variables: { input },
        });

        if (result.data?.savePatientAndCaregiverInfoOnReferral) {
          const savedAppealId = appealQueryData?.getAppealById?.appealId;

          if (values.consentDocument && savedAppealId) {
            const pageCount = await getPageCountForFile(values.consentDocument);
            const documentResult = await upsertDocument({
              variables: {
                appealId: savedAppealId,
                document: {
                  documentType: AppealSupportingDocumentType.PatientConsent,
                  files: [
                    {
                      fileName: values.consentDocument.name,
                      fileType:
                        values.consentDocument.type === "application/pdf"
                          ? AppealSupportingDocumentFileType.Pdf
                          : AppealSupportingDocumentFileType.Image,
                      pageCount,
                    },
                  ],
                },
              },
            });

            const uploadUrls =
              documentResult.data?.upsertAppealSupportingDocument?.uploadUrls;
            if (uploadUrls && uploadUrls.length > 0) {
              await uploadFileToUrl(values.consentDocument, uploadUrls[0]);
            }
          }

          resetFormDirty();
          return true;
        }
        return false;
      } catch (error) {
        const clientSafeMessage = getClientSafeErrorMessage(error);
        setError(clientSafeMessage ?? "An error occurred. Please try again.");
        return false;
      }
    };

    useImperativeHandle(ref, () => ({
      submit: async () => {
        const validationResult = form.validate();
        if (validationResult.hasErrors) {
          return false;
        }

        const providerNeedsConsent = !providerData?.provider?.hasSignedBAA;

        if (providerNeedsConsent && !form.values.hasPatientConsent) {
          form.setFieldError(
            "hasPatientConsent",
            "Patient consent is required",
          );
          const consentElement = document.querySelector(
            "[data-consent-section]",
          );
          if (consentElement) {
            consentElement.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
          return false;
        }

        setFormSubmissionAttempted(true);
        return await handleFormSubmit(form.getValues());
      },
    }));

    if (appealQueryLoading) {
      return (
        <div className={styles.container}>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "40px",
            }}
          >
            <Loader color="loader_almost_black" width="36" height="36" />
          </div>
        </div>
      );
    }

    return (
      <div className={styles.container}>
        {error && (
          <Alert color="red" title="Error" style={{ marginBottom: "16px" }}>
            {error}
          </Alert>
        )}
        <div className={styles.leftColumnWrapper}>
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h3 className={styles.cardTitle}>Patient</h3>
              <p className={styles.cardSubtitle}>
                Tell us about your patient and we'll send them an email inviting
                them to Claimable. See a sample{" "}
                <a
                  href="/images/product_illustrations/sample_email.png"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="View sample email (opens in new window)"
                >
                  email here
                </a>
                .
              </p>
            </div>
            <fieldset className={styles.formSection}>
              <legend className={styles.srOnly}>Patient Information</legend>
              <TextInput
                label="First Name"
                required
                withAsterisk={false}
                readOnly={readOnly}
                disabled={readOnly}
                data-testid="patient-first-name-input"
                {...form.getInputProps("firstName")}
                className={styles.field}
              />
              <TextInput
                label="Last Name"
                required
                withAsterisk={false}
                readOnly={readOnly}
                disabled={readOnly}
                data-testid="patient-last-name-input"
                {...form.getInputProps("lastName")}
                className={styles.field}
              />
              <DateInput
                label="Date of Birth"
                valueFormat="MM/DD/YYYY"
                data-testid="patient-dob-input"
                {...form.getInputProps("dateOfBirth")}
                required
                withAsterisk={false}
                readOnly={readOnly}
                disabled={readOnly}
                className={styles.field}
                defaultDate={dateConstraints.maxDate}
                defaultLevel="decade"
                minDate={dateConstraints.minDate}
                maxDate={dateConstraints.maxDate}
                rightSection={
                  <FontAwesomeIcon
                    icon={faCalendar}
                    style={{ fontSize: "16px", color: "#868E96" }}
                  />
                }
              />
              {showCaregiverForm && (
                <>
                  <Select
                    label="Sex"
                    required
                    withAsterisk={false}
                    data={sexOptions}
                    readOnly={readOnly}
                    disabled={readOnly}
                    data-testid="patient-sex-input"
                    {...form.getInputProps("sex")}
                    className={styles.field}
                    placeholder="Select sex"
                  />
                  <Select
                    label="Pronouns"
                    required
                    withAsterisk={false}
                    data={pronounsOptions}
                    readOnly={readOnly}
                    disabled={readOnly}
                    data-testid="patient-pronouns-input"
                    {...form.getInputProps("pronouns")}
                    className={styles.field}
                    placeholder="Select pronouns"
                  />
                </>
              )}
              <Select
                label="State of Residence"
                required
                withAsterisk={false}
                data={stateOptions}
                searchable
                readOnly={readOnly}
                disabled={readOnly}
                data-testid="patient-state-input"
                {...form.getInputProps("state")}
                className={styles.field}
              />
              {!showCaregiverForm && (
                <>
                  <TextInput
                    label="Email Address"
                    required
                    withAsterisk={false}
                    type="email"
                    readOnly={readOnly}
                    disabled={readOnly}
                    data-testid="patient-email-input"
                    {...form.getInputProps("email")}
                    className={styles.field}
                  />
                  <div>
                    <TextInput
                      label={mobileNumberLabel}
                      readOnly={readOnly}
                      disabled={readOnly}
                      data-testid="patient-phone-input"
                      {...form.getInputProps("phoneNumber")}
                      onChange={handlePhoneNumberChange}
                      className={styles.field}
                    />
                    <p className={styles.phoneHelpText}>
                      We use their number to contact them if their appeal needs
                      support.
                    </p>
                  </div>
                  <div className={styles.checkboxSection}>
                    <Checkbox
                      label={`We have collected consent: "${SMS_NOTIFICATIONS_TRANSACTIONAL_CONSENT_LANGUAGE}"`}
                      classNames={{
                        inner: styles.checkboxContainer,
                        input: styles.checkboxInput,
                        icon: styles.checkboxIcon,
                      }}
                      readOnly={readOnly}
                      disabled={readOnly}
                      data-testid="patient-sms-consent-checkbox"
                      {...form.getInputProps("hasConsentedToSMSNotifications", {
                        type: "checkbox",
                      })}
                      onChange={handleSMSConsentChange}
                    />
                    <Checkbox
                      label={`We have collected consent: "${SMS_NOTIFICATIONS_MARKETING_CONSENT_LANGUAGE}"`}
                      classNames={{
                        inner: styles.checkboxContainer,
                        input: styles.checkboxInput,
                        icon: styles.checkboxIcon,
                      }}
                      readOnly={readOnly}
                      disabled={readOnly}
                      data-testid="patient-marketing-sms-consent-checkbox"
                      {...form.getInputProps("hasConsentedToMarketingSMS", {
                        type: "checkbox",
                      })}
                    />
                  </div>
                </>
              )}
            </fieldset>
          </div>

          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h3 className={styles.cardTitle}>
                Personal message for your patient
              </h3>
            </div>
            <Textarea
              placeholder="Write a personal message to your patient that we'll include in the welcome email. (Optional)"
              minRows={4}
              readOnly={readOnly}
              disabled={readOnly}
              {...form.getInputProps("message")}
              classNames={{ input: styles.textareaWithHeight }}
            />
          </div>
        </div>

        <div className={styles.rightColumnWrapper}>
          {!showCaregiverForm &&
            isCaregiverAllowedForCondition(
              appealQueryData?.getAppealById?.denial?.condition,
              medicationBrandName,
            ) && (
              <div className={styles.addCaregiverSection}>
                <div className={styles.addCaregiverHeader}>
                  <h3 className={styles.addCaregiverTitle}>Caregiver</h3>
                  <button
                    type="button"
                    className={styles.addCaregiverButton}
                    onClick={handleToggleCaregiverSection}
                    disabled={readOnly}
                  >
                    Add
                  </button>
                </div>
                <p className={styles.addCaregiverDescription}>
                  Add if the patient is a minor or has a legal representative
                  appealing on their behalf.
                </p>
              </div>
            )}

          {showCaregiverForm && (
            <div className={styles.caregiverCard}>
              <div className={styles.card}>
                <div className={styles.cardHeader}>
                  <h3 className={styles.cardTitle}>Caregiver</h3>
                </div>
                <fieldset className={styles.formSection}>
                  <legend className={styles.srOnly}>
                    Caregiver Information
                  </legend>
                  <TextInput
                    label="First Name"
                    required
                    withAsterisk={false}
                    readOnly={readOnly}
                    disabled={readOnly}
                    data-testid="caregiver-first-name-input"
                    {...form.getInputProps("caregiverFirstName")}
                    className={styles.field}
                  />
                  <TextInput
                    label="Last Name"
                    required
                    withAsterisk={false}
                    readOnly={readOnly}
                    disabled={readOnly}
                    data-testid="caregiver-last-name-input"
                    {...form.getInputProps("caregiverLastName")}
                    className={styles.field}
                  />
                  <TextInput
                    label="Email Address"
                    required
                    withAsterisk={false}
                    type="email"
                    readOnly={readOnly}
                    disabled={readOnly}
                    data-testid="caregiver-email-input"
                    {...form.getInputProps("email")}
                    className={styles.field}
                  />
                  <DateInput
                    label="Date of Birth"
                    valueFormat="MM/DD/YYYY"
                    data-testid="caregiver-dob-input"
                    {...form.getInputProps("caregiverDateOfBirth")}
                    withAsterisk={false}
                    readOnly={readOnly}
                    disabled={readOnly}
                    className={styles.field}
                    defaultDate={dateConstraints.maxDate}
                    defaultLevel="decade"
                    minDate={dateConstraints.minDate}
                    maxDate={dateConstraints.maxDate}
                    rightSection={
                      <FontAwesomeIcon
                        icon={faCalendar}
                        style={{ fontSize: "16px", color: "#868E96" }}
                      />
                    }
                  />
                  <Select
                    label="Relationship to Patient"
                    required
                    withAsterisk={false}
                    data={relationshipOptions}
                    readOnly={readOnly}
                    disabled={readOnly}
                    data-testid="caregiver-relationship-input"
                    {...form.getInputProps("caregiverRelationship")}
                    className={styles.field}
                    placeholder="Select relationship"
                  />
                  <div>
                    <TextInput
                      label={mobileNumberLabel}
                      readOnly={readOnly}
                      disabled={readOnly}
                      data-testid="caregiver-phone-input"
                      {...form.getInputProps("phoneNumber")}
                      onChange={handlePhoneNumberChange}
                      className={styles.field}
                    />
                    <p className={styles.phoneHelpText}>
                      We use their number to contact them if their appeal needs
                      support.
                    </p>
                  </div>
                  <div className={styles.checkboxSection}>
                    <Checkbox
                      label={`We have collected consent: "${SMS_NOTIFICATIONS_TRANSACTIONAL_CONSENT_LANGUAGE}"`}
                      classNames={{
                        inner: styles.checkboxContainer,
                        input: styles.checkboxInput,
                        icon: styles.checkboxIcon,
                      }}
                      readOnly={readOnly}
                      disabled={readOnly}
                      data-testid="caregiver-sms-consent-checkbox"
                      {...form.getInputProps("hasConsentedToSMSNotifications", {
                        type: "checkbox",
                      })}
                      onChange={handleSMSConsentChange}
                    />
                    <Checkbox
                      label={`We have collected consent: "${SMS_NOTIFICATIONS_MARKETING_CONSENT_LANGUAGE}"`}
                      classNames={{
                        inner: styles.checkboxContainer,
                        input: styles.checkboxInput,
                        icon: styles.checkboxIcon,
                      }}
                      readOnly={readOnly}
                      disabled={readOnly}
                      data-testid="caregiver-marketing-sms-consent-checkbox"
                      {...form.getInputProps("hasConsentedToMarketingSMS", {
                        type: "checkbox",
                      })}
                    />
                  </div>
                </fieldset>
              </div>
              <div className={styles.removeCaregiverSection}>
                <button
                  type="button"
                  onClick={handleToggleCaregiverSection}
                  disabled={readOnly}
                >
                  Remove Caregiver
                </button>
              </div>
            </div>
          )}

          {appealId && !providerData?.provider?.hasSignedBAA && (
            <PatientConsentSection
              appealId={appealId}
              hasPatientConsent={form.values.hasPatientConsent}
              onConsentChange={handleConsentChange}
              disabled={readOnly}
              hasError={form.errors.hasPatientConsent ? true : false}
              existingDocument={existingConsentDocument}
            />
          )}
        </div>
      </div>
    );
  },
);

PatientInfoForm.displayName = "PatientInfoForm";

export default PatientInfoForm;
